import _camelCase from 'lodash/camelCase';
import _concat from 'lodash/concat';
import _defaultTo from 'lodash/defaultTo';
import _every from 'lodash/every';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _keys from 'lodash/keys';
import _map from 'lodash/map';
import _orderBy from 'lodash/orderBy';
import _reduce from 'lodash/reduce';
import _size from 'lodash/size';
import _snakeCase from 'lodash/snakeCase';
import _sortBy from 'lodash/sortBy';
import _toString from 'lodash/toString';
import _transform from 'lodash/transform';
import _values from 'lodash/values';
// Serialization
import { deserialize, serialize } from 'serializr';
import {
  deviceConfigSchema,
  deviceDistributionHistorySchema,
  deviceInstallationDeviceSchema,
  deviceSchema,
  deviceSlaveSchema,
  deviceSmsSchema,
  deviceSyncStatusSchema,
} from './schemas';
// Colors
import {
  deviceIOStatePalette,
  deviceNetworkPalette,
  iconPalette,
  statusPalette,
} from '../components/core/colors';
// Permissions
import permissions from '../constants/Permissions';
// Models
import mEquipment from './equipment';
// Custom
import { SEARCHFIELD_TYPES } from '../constants/App';
import {
  joinNonEmpty, defaultIcon, findInList, formatPhoneNumberSubmit,
} from '../utils/Tools';
import { dateGenerator, dateFormatter } from '../utils/DateUtils';

/**
* Serialization
*/
const serializerKey = 'device';
const serializerKeyDeviceSlave = 'device_slave';
const serializerKeyConfig = 'config';
const serializerKeySms = 'sms';

const deserializeDeviceDistributionHistory = (data) => deserialize(deviceDistributionHistorySchema, data);

const deserializeDeviceSyncStatus = (data) => deserialize(deviceSyncStatusSchema, data);

const deserializeDevice = (data) => deserialize(deviceSchema, data);

const serializeDevice = ({ companyId }) => ({
  [serializerKey]: {
    ...serialize(deviceSchema, {
      company_id: _defaultTo(companyId, null),
    }),
  },
});

const serializeDeviceInstallationDevice = ({ name, disabled }) => ({
  ...serialize(deviceInstallationDeviceSchema, {
    name: _defaultTo(name, null),
    ...(!_isNil(disabled) && { disabled }),
  }),
});

const serializeDeviceSlave = ({
  name, triggers, relays, inputs,
}) => ({
  [serializerKeyDeviceSlave]: {
    ...serialize(deviceSlaveSchema, {
      ...(name && { name }),
      ...(triggers && { triggers }),
      ...(relays && { relays }),
      ...(inputs && { inputs }),
    }),
  },
});

const serializeDeviceConfig = ({
  serverUrl, monitoringTimer, ignorePlateDuration, tcpPort, udpPort, periodicSynchronizationTimer, defaultOperatorId, logLevel, equipmentsNetworkIp, operatorsBlacklist, operatorsWhitelistCountries,
}) => ({
  [serializerKeyConfig]: {
    ...serialize(deviceConfigSchema, {
      server_url: _defaultTo(serverUrl, null),
      monitoring_timer: _defaultTo(monitoringTimer, null),
      tcp_port: _defaultTo(tcpPort, null),
      udp_port: _defaultTo(udpPort, null),
      ignore_plate_duration: _defaultTo(ignorePlateDuration, null),
      periodic_synchronization_timer: _defaultTo(periodicSynchronizationTimer, null),
      default_operator_id: _defaultTo(defaultOperatorId, null),
      log_level: _defaultTo(logLevel, null),
      equipments_network_ip: _defaultTo(equipmentsNetworkIp, null),
      ...(operatorsBlacklist && { operators_blacklist: operatorsBlacklist }),
      ...(operatorsWhitelistCountries && { operators_whitelist_countries: operatorsWhitelistCountries }),
    }),
  },
});

const deserializeDeviceSms = (data) => deserialize(deviceSmsSchema, data);

const serializeDeviceSms = ({ type, respondTo, data }) => ({
  [serializerKeySms]: {
    ...serialize(deviceSmsSchema, {
      type: _defaultTo(type, null),
      ...(respondTo && { respond_to: formatPhoneNumberSubmit(respondTo) }),
      ...(data && { data }),
    }),
  },
});

/**
* Lists
*/
const deviceVirtualTriggerEthernet = {
  type: 'ethernet',
  protocols: ['ethernet'],
};

const deviceStatusList = [
  {
    key: 'timeout_false',
    value: false,
    labelId: 'device.timeout.false',
    icon: 'deviceOnline',
    color: statusPalette.success,
  },
  {
    key: 'timeout_true',
    value: true,
    labelId: 'device.timeout.true',
    icon: 'deviceOffline',
    color: statusPalette.warning,
  },
];

const deviceSlaveTriggerEquipmentStatusList = [
  {
    key: 'ok',
    value: 'ok',
    labelId: 'device.equipmentStatus.ok',
    icon: 'deviceEquStatusOk',
    color: statusPalette.success,
  },
  {
    key: 'nok',
    value: 'nok',
    labelId: 'device.equipmentStatus.nok',
    icon: 'deviceEquStatusNok',
    color: statusPalette.warning,
  },
];

const deviceRelayStatesList = [
  {
    key: 'none',
    value: 'na',
    labelId: 'relay.state.none',
    icon: 'dRelayStateNone',
  },
  {
    key: '1',
    value: '1',
    labelId: 'relay.state.1',
    icon: 'dRelayState1',
    color: deviceIOStatePalette.up,
  },
  {
    key: '0',
    value: '0',
    labelId: 'relay.state.0',
    icon: 'dRelayState0',
    color: deviceIOStatePalette.down,
  }, /* ,
  {
    key: 'bs',
    value: '~',
    labelId: 'relay.state.bistable',
    icon: 'dRelayStateBs',
    color: deviceIOStatePalette.bistable,
  } */
];

const deviceInputStatesList = [
  {
    key: 'none',
    value: 'na',
    labelId: 'input.state.none',
    icon: 'dInputStateNone',
  },
  {
    key: '0',
    value: '0',
    labelId: 'input.state.0',
    icon: 'dInputState0',
    color: deviceIOStatePalette.down,
  },
  {
    key: '1',
    value: '1',
    labelId: 'input.state.1',
    icon: 'dInputState1',
    color: deviceIOStatePalette.up,
  },
];

const deviceInputMovetoStatesList = [
  {
    key: 'none',
    value: 'na',
    labelId: 'input.state.none',
    icon: 'dInputStateNone',
  },
  {
    key: '0to1',
    value: '0to1',
    labelId: 'input.state.0to1',
    icon: 'dInputState0to1',
    color: deviceIOStatePalette.up,
  },
  {
    key: '1to0',
    value: '1to0',
    labelId: 'input.state.1to0',
    icon: 'dInputState1to0',
    color: deviceIOStatePalette.down,
  },
];

const deviceCommands = {
  ping: 'ping',
  sync: 'sync',
  reboot: 'reboot',
  reset: 'reset',
  status: 'status',
  operatorsConfig: 'operators_config',
};

const deviceSmsTypesList = [
  {
    key: deviceCommands.sync,
    value: deviceCommands.sync,
    labelId: 'button.synchronize',
    icon: 'dSmsTypeSync',
    needRespondTo: false,
  },
  {
    key: deviceCommands.reboot,
    value: deviceCommands.reboot,
    labelId: 'button.reboot',
    icon: 'dSmsTypeReboot',
    needRespondTo: false,
  },
  {
    key: deviceCommands.reset,
    value: deviceCommands.reset,
    labelId: 'button.reset',
    icon: 'dSmsTypeReset',
    needRespondTo: false,
  },
  {
    key: deviceCommands.status,
    value: deviceCommands.status,
    labelId: 'button.getStatus',
    icon: 'dSmsTypeStatus',
    needRespondTo: true,
  },
  {
    key: deviceCommands.operatorsConfig,
    value: deviceCommands.operatorsConfig,
    labelId: 'button.operatorsConfig',
    icon: 'dSmsTypeOperatorsConfig',
    needRespondTo: false,
  },
];

const deviceSmsStatusList = [
  {
    key: 'pending',
    value: 'pending',
    labelId: 'deviceSms.status.pending',
    icon: 'pending',
    color: statusPalette.pending,
  },
  {
    key: 'delivered',
    value: 'delivered',
    labelId: 'deviceSms.status.delivered',
    icon: 'success',
    color: statusPalette.success,
  },
  {
    key: 'error',
    value: 'error',
    labelId: 'deviceSms.status.error',
    icon: 'warning',
    color: statusPalette.warning,
  },
];

const deviceNetworkAccessTechnologiesList = [
  {
    key: 'gsm',
    value: 'gsm',
    label: '2G',
    icon: 'sigTech2g',
  },
  {
    key: 'gsm_compact',
    value: 'gsm_compact',
    label: '2G',
    icon: 'sigTech2g',
  },
  {
    key: 'utran',
    value: 'utran',
    label: '3G',
    icon: 'sigTech3g',
  },
  {
    key: 'eutran',
    value: 'eutran',
    label: '4G',
    icon: 'sigTech4g',
  },
];

/**
* Utils
*/
const sortDevices = (devices, fields = ['serial'], dirs = ['asc']) => (_orderBy(devices, fields, dirs));
const sortDeviceSlaves = (slaves, fields = ['serial'], dirs = ['asc']) => (_orderBy(slaves, fields, dirs));
const sortDeviceTriggers = (triggers, fields = ['protocols', 'type', 'equipment.name'], dirs = ['desc', 'asc', 'asc']) => (_orderBy(triggers, fields, dirs));
const sortDeviceRelays = (relays, fields = ['code'], dirs = ['asc']) => (_orderBy(relays, fields, dirs));
const sortDeviceInputs = (inputs, fields = ['code'], dirs = ['asc']) => (_orderBy(inputs, fields, dirs));
const sortDeviceSmsHistory = (smsList, fields = ['date'], dirs = ['desc']) => (_orderBy(smsList, fields, dirs));

const deviceTitle = (deviceData) => _get(deviceData, 'serial', '');

const deviceMinimize = (device) => ({ id: device.id, serial: device.serial });

const deviceNetworkLevelIcon = (networkLevel) => {
  let icon = 'sigSthUnknown';
  let color = iconPalette.base;
  switch (_toString(networkLevel)) {
    case '0':
      icon = 'sigSth0';
      color = deviceNetworkPalette.level0;
      break;
    case '1':
      icon = 'sigSth1';
      color = deviceNetworkPalette.level1;
      break;
    case '2':
      icon = 'sigSth2';
      color = deviceNetworkPalette.level2;
      break;
    case '3':
      icon = 'sigSth3';
      color = deviceNetworkPalette.level3;
      break;
    case '4':
      icon = 'sigSth4';
      color = deviceNetworkPalette.level4;
      break;
    case '5':
      icon = 'sigSth5';
      color = deviceNetworkPalette.level5;
      break;
    default:
      break;
  }

  return { icon, color };
};

const deviceInstallationName = (device) => (device ? (_get(device, 'installation.device.name') || '') : '');
const deviceInstallationDesc = (device) => (device ? joinNonEmpty([device.serial, deviceInstallationName(device)], ' - ') : '');
const deviceIsDisabled = (device) => !!_get(device, 'installation.device.disabled');

const deviceSlaveFromRelay = (idRelay, device) => {
  if (!device) {
    return null;
  }
  let retSlave;
  _forEach(device.device_slaves, (slave) => {
    if (_find(slave.relays, ['id', idRelay])) {
      retSlave = slave;
    }
  });
  return retSlave;
};

const deviceRelays = (device) => {
  if (!device) {
    return null;
  }
  return _reduce(device.device_slaves, (result, slave) => (_concat(result, slave.relays)), []);
};

const deviceRelay = (idRelay, device) => _find(deviceRelays(device), ['id', idRelay]);

const deviceSlaveFromInput = (idInput, device) => {
  if (!device) {
    return null;
  }
  let retSlave;
  _forEach(device.device_slaves, (slave) => {
    if (_find(slave.inputs, ['id', idInput])) {
      retSlave = slave;
    }
  });
  return retSlave;
};

const deviceInputs = (device) => {
  if (!device) {
    return null;
  }
  return _reduce(device.device_slaves, (result, slave) => (_concat(result, slave.inputs)), []);
};

const deviceInput = (idInput, device) => _find(deviceInputs(device), ['id', idInput]);

const deviceSlaveFromTrigger = (idTrigger, device) => {
  if (!device) {
    return null;
  }
  let retSlave;
  _forEach(device.device_slaves, (slave) => {
    if (_find(slave.triggers, ['id', idTrigger])) {
      retSlave = slave;
    }
  });
  return retSlave;
};

const deviceTriggers = (device) => {
  if (!device) {
    return null;
  }
  return _reduce(device.device_slaves, (result, slave) => (_concat(result, sortDeviceTriggers(slave.triggers))), []);
};

const deviceTrigger = (idTrigger, device) => _find(deviceTriggers(device), ['id', idTrigger]);

const deviceTriggerFromDevices = (idTrigger, devices) => {
  let foundTrigger;
  _forEach(devices, (device) => {
    const trigger = deviceTrigger(idTrigger, device);
    if (trigger) {
      foundTrigger = trigger;
    }
  });
  return foundTrigger;
};

const deviceTriggerCanHaveEquipement = (trigger) => (_includes(['e1', 'e2', 'serial1', 'serial2', 'ethernet'], trigger.type));
const deviceTriggerCanHaveFallbackTrigger = (trigger) => (_includes(['e1', 'e2'], trigger.type));
const deviceTriggerGetKeypadEquipment = (triggers) => _filter(triggers, (trigger) => _includes(['e1', 'e2'], trigger.type));

const deviceTriggerIcon = (trigger) => {
  if (!trigger) {
    return 'dtrUnknown';
  }
  if (trigger.equipment) {
    return mEquipment.type(trigger.equipment).icon;
  }
  switch (trigger.type) {
    case 'api':
      return 'dtrApi';
    case 'gsm':
      return 'dtrGsm';
    default:
      return 'dtrUnknown';
  }
};

const deviceStatus = (device, useDisabled = true) => {
  if (!device) {
    return defaultIcon;
  }
  // Base status
  const status = _find(deviceStatusList, ['value', !_isNil(device.timeout) ? device.timeout : true]);
  // Device disabled => use "ignored" color
  if (useDisabled && device.disabled) {
    return {
      ...status,
      color: statusPalette.ignored,
    };
  }
  return status;
};

const deviceSynchronized = (device) => {
  if (!device || !device.sync_status) {
    return false;
  }
  return _every(_values(device.sync_status), (v) => !!v);
};

const deviceSmsType = (sms) => findInList(deviceSmsTypesList, 'value', sms, 'type');
const deviceSmsStatus = (sms) => findInList(deviceSmsStatusList, 'value', sms, 'delivery_status');
const deviceNetworkAccessTechnology = (networkAccessTechnology) => {
  if (!networkAccessTechnology) {
    return null;
  }
  return _find(deviceNetworkAccessTechnologiesList, ['value', _snakeCase(networkAccessTechnology)]);
};

// This array will contains objects like {deviceId, date}
let updatedDevicesDates = [];

const updateDeviceIsNeeded = (id, data, localUpdatedDevicesDates = updatedDevicesDates, localNow = dateGenerator.now()) => {
  // This function is called by deviceData reducer to check if state must be updated after receiving a EVENT_INFO from websocket
  // Actually on big synchros, we receive for concerned device a lot of EVENT_INFO messages, most of them containing only a
  // {last_comm_date: "XXXX"} payload, which can slow down UI because of multiple new renders
  let updateIsNeeded = true;
  // The check must be done only if payload contains one key 'last_comm_date'
  const dataKeys = _keys(data);
  if (_size(dataKeys) === 1 && dataKeys[0] === 'last_comm_date') {
    // Find last date when the device was updated
    const lastDeviceDate = _find(localUpdatedDevicesDates, ['deviceId', id]);
    if (lastDeviceDate) {
      // Found => compare dates
      const { date } = lastDeviceDate;
      const diff = dateFormatter.diff(localNow, date);
      // Perform update only if previous one occured more than 2 seconds ago
      if (diff < 2000) {
        updateIsNeeded = false;
      }
      // Update time of last update for this device
      updatedDevicesDates = _map(updatedDevicesDates, (deviceDate) => (deviceDate.deviceId === id ? {
        ...deviceDate,
        date: localNow,
      } : deviceDate));
    } else {
      // Not found => store
      updatedDevicesDates.push({ deviceId: id, date: localNow });
    }
  }

  return updateIsNeeded;
};

const updateDeviceFromServerData = (device, data) => {
  // Create a copy of device
  let updatedDevice = { ...device };
  // Get data elements
  const {
    timeout,
    last_comm_date: lastCommDate,
    network_level: networkLevel,
    network_operator: networkOperator,
    network_access_technology: networkAccessTechnology,
    sync_status: syncStatus,
  } = data;
  // Update it according to current data and received data
  // timeout
  if (_has(updatedDevice, 'timeout') && !_isNil(timeout)) {
    updatedDevice = {
      ...updatedDevice,
      timeout,
    };
  }
  // last_comm_date
  if (_has(updatedDevice, 'last_comm_date') && !_isNil(lastCommDate)) {
    updatedDevice = {
      ...updatedDevice,
      last_comm_date: lastCommDate,
    };
  }
  // info
  if (_has(updatedDevice, 'info')) {
    if (!_isNil(networkLevel)) {
      updatedDevice = {
        ...updatedDevice,
        info: {
          ...updatedDevice.info,
          network_level: networkLevel,
        },
      };
    }
    if (!_isNil(networkOperator)) {
      updatedDevice = {
        ...updatedDevice,
        info: {
          ...updatedDevice.info,
          network_operator: networkOperator,
        },
      };
    }
    if (!_isNil(networkAccessTechnology)) {
      updatedDevice = {
        ...updatedDevice,
        info: {
          ...updatedDevice.info,
          network_access_technology: networkAccessTechnology,
        },
      };
    }
  }
  // sync_status
  if (_has(updatedDevice, 'sync_status') && !_isNil(syncStatus)) {
    updatedDevice = {
      ...updatedDevice,
      sync_status: syncStatus,
    };
  }
  // Finally return updated object
  return updatedDevice;
};

const deviceSlaveTriggerEquipmentStatus = (equipment) => findInList(deviceSlaveTriggerEquipmentStatusList, 'value', equipment, 'status');

const deviceSyncStatusOrder = {
  config: 1,
  apns: 2,
  installations: 3,
  entities: 4,
  slaves: 5,
  scenarios: 6,
  tasks: 7,
  plannings: 8,
  parkingCategories: 9,
  allotments: 10,
  customers: 11,
  antipassbacks: 12,
};

const deviceSyncStatusToArray = (device) => {
  const { sync_status: syncStatus } = device;
  if (syncStatus) {
    return (
      _sortBy(
        _transform(syncStatus, (result, value, key) => {
          result.push({
            item: _camelCase(key),
            sync: value,
            deprecated: key === 'antipassbacks',
          });
        }, []), ({ item }) => (
          _get(deviceSyncStatusOrder, item)
        ),
      )
    );
  }
  return [];
};

/**
* Model
*/
const device = {
  // Name and icon
  name: 'device',
  icon: 'device',

  // Serialization
  deserialize: deserializeDevice,
  serialize: serializeDevice,
  serializeSlave: serializeDeviceSlave,
  serializeConfig: serializeDeviceConfig,
  serializeInstallationDevice: serializeDeviceInstallationDevice,
  deserializeSms: deserializeDeviceSms,
  serializeSms: serializeDeviceSms,
  deserializeSyncStatus: deserializeDeviceSyncStatus,
  deserializeDistributionHistory: deserializeDeviceDistributionHistory,

  // Sort elements
  sortElements: sortDevices,
  sortSlaves: sortDeviceSlaves,
  sortTriggers: sortDeviceTriggers,
  sortRelays: sortDeviceRelays,
  sortInputs: sortDeviceInputs,
  sortSmsHistory: sortDeviceSmsHistory,

  // Lists
  virtualTriggerEthernet: deviceVirtualTriggerEthernet,
  statusList: deviceStatusList,
  slaveTriggerEquipmentStatusList: deviceSlaveTriggerEquipmentStatusList,
  relayStatesList: deviceRelayStatesList,
  inputStatesList: deviceInputStatesList,
  inputMovetoStatesList: deviceInputMovetoStatesList,
  commands: deviceCommands,
  smsTypesList: deviceSmsTypesList,
  smsStatusList: deviceSmsStatusList,
  networkAccessTechnologiesList: deviceNetworkAccessTechnologiesList,

  // Utils
  title: deviceTitle,
  minimize: deviceMinimize,
  installationName: deviceInstallationName,
  installationDesc: deviceInstallationDesc,
  isDisabled: deviceIsDisabled,
  networkLevelIcon: deviceNetworkLevelIcon,
  slaveFromRelay: deviceSlaveFromRelay,
  relays: deviceRelays,
  relay: deviceRelay,
  slaveFromInput: deviceSlaveFromInput,
  inputs: deviceInputs,
  input: deviceInput,
  slaveFromTrigger: deviceSlaveFromTrigger,
  triggers: deviceTriggers,
  trigger: deviceTrigger,
  triggerFromDevices: deviceTriggerFromDevices,
  triggerCanHaveEquipement: deviceTriggerCanHaveEquipement,
  triggerCanHaveFallbackTrigger: deviceTriggerCanHaveFallbackTrigger,
  triggerGetKeypadEquipment: deviceTriggerGetKeypadEquipment,
  triggerIcon: deviceTriggerIcon,
  status: deviceStatus,
  synchronized: deviceSynchronized,
  smsType: deviceSmsType,
  smsStatus: deviceSmsStatus,
  networkAccessTechnology: deviceNetworkAccessTechnology,
  updateIsNeeded: updateDeviceIsNeeded,
  updateFromServerData: updateDeviceFromServerData,
  slaveTriggerEquipmentStatus: deviceSlaveTriggerEquipmentStatus,
  syncStatusToArray: deviceSyncStatusToArray,

  // List features
  listFeatures: ({ withCompany = true } = {}) => (
    {
    // Default list params
      defaultParams: {
        pagination: {
          page: 1,
          pageSize: 20,
        },
        sort: [
          { field: 'serial', dir: 'asc' },
        ],
      },

      // Autocomplete filter
      autoCompleteFilter: {
        field: 'serial',
        operator: SEARCHFIELD_TYPES.string.defaultOperator(),
      },

      // Columns
      columnsQuantity: (hasPermission) => ({
        xs: 2,
        sm: 4,
        md: 4,
        lg: 7 + (hasPermission({ needed: permissions.pAdmin }) | 0),
        xl: 7 + (hasPermission({ needed: permissions.pAdmin }) | 0) * 2 + (withCompany && hasPermission({ needed: permissions.pAdmin }) | 0),
      }),
      columns: [
        {
          key: 'serial',
          labelId: 'field.serial',
          field: 'serial',
          sortable: true,
        },
        {
          key: 'device_model',
          labelId: 'field.deviceModel',
          field: 'device_model',
          sortable: false,
          className: 'hidden-md-down',
        },
        {
          key: 'device_distribution_maps',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.distributions',
          field: 'device_distribution_maps',
          sortable: false,
          className: 'hidden-md-down',
        },
        {
          key: 'timeout',
          labelId: 'field.status',
          field: 'timeout',
          alias: 'status',
          sortable: true,
          className: 'table-content-1',
        },
        {
          key: 'last_comm_date',
          labelId: 'field.lastComDate',
          field: 'last_comm_date',
          sortable: true,
          className: 'hidden-xs-down',
        },
        {
          key: 'network',
          labelId: 'field.network',
          field: 'network_level',
          sortable: false,
          className: 'hidden-xs-down',
        },
        {
          key: 'installation',
          labelId: 'field.installation',
          field: 'installation_name',
          sortable: true,
          className: 'hidden-md-down',
        },
        {
          key: 'simcard',
          labelId: 'field.simcard',
          field: 'simcard',
          sortable: false,
          className: 'hidden-md-down',
        },
        {
          key: 'device_production_batch',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.productionBatch',
          field: 'device_production_batch_id',
          sortable: false,
          className: 'hidden-lg-down',
        },
        {
          key: 'company',
          hidden: !withCompany,
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.company',
          field: 'company',
          sortable: false,
          className: 'hidden-lg-down',
        },
      ],

      // Search fields
      searchFields: [
        {
          key: 'serial',
          labelId: 'field.serial',
          field: 'serial',
          initial: '',
          isUndefined: (value) => _isEmpty(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.string,
          filter: {
            field: 'serial',
          },
        },
        {
          key: 'timeout',
          labelId: 'field.status',
          field: 'timeout',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.boolean,
          filter: {
            field: 'timeout',
          },
        },
        {
          key: 'deviceModelId',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.deviceModel',
          field: 'deviceModelId',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'device_model_id',
          },
        },
        {
          key: 'distribution',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.distribution',
          field: 'distribution',
          alias: 'distribution_id',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: true,
          excludeFilterFields: ['distribution', 'device_distribution_maps.distribution_id'],
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'device_distribution_maps.distribution_id',
            key: 'id',
          },
        },
        {
          key: 'beginLastComm',
          labelId: 'field.afterLastComm',
          field: 'beginLastComm',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.date,
          scope: 'after',
          filter: {
            field: 'last_comm_date',
          },
        },
        {
          key: 'endLastComm',
          labelId: 'field.beforeLastComm',
          field: 'endLastComm',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.date,
          scope: 'before',
          filter: {
            field: 'last_comm_date',
          },
        },
        {
          key: 'available',
          labelId: 'field.installationAttached',
          field: 'available',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          excludeFilterFields: ['available', 'installation', 'companyId'],
          type: SEARCHFIELD_TYPES.boolean,
          filter: {
            field: 'available',
          },
        },
        {
          key: 'installation',
          permissions: {
            needed: permissions.pInstallation.read,
            permissionsOrContext: 'userPermissions',
          },
          labelId: 'field.installation',
          field: 'installation',
          alias: 'installation_id',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: true,
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'installation_device_maps.installation_id',
            key: 'id',
          },
        },
        {
          key: 'simcardStatus',
          labelId: 'field.simcardStatus',
          field: 'simcardStatus',
          alias: 'simcard_status',
          initial: null,
          isUndefined: (value) => _isNil(value),
          excludeFilterFields: ['simcardStatus', 'device_simcard_maps.simcard.status'],
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.enum,
          filter: {
            field: 'device_simcard_maps.simcard.status',
          },
        },
        {
          key: 'simcard',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.simcard',
          field: 'simcard',
          alias: 'simcard_id',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: true,
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'device_simcard_maps.simcard_id',
            key: 'id',
          },
        },
        {
          key: 'operatorId',
          labelId: 'field.operator',
          field: 'operatorId',
          alias: 'operator_id',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'device_info.network_operator_id',
          },
        },
        {
          key: 'deviceBatchId',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.productionBatch',
          field: 'deviceBatchId',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'device_production_batch_id',
          },
        },
        {
          key: 'ipAddress',
          labelId: 'field.ipAddress',
          field: 'ipAddress',
          initial: '',
          isUndefined: (value) => _isEmpty(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.string,
          scope: 'like',
          filter: {
            field: 'ip_address',
          },
        },
        {
          key: 'companyId',
          permissions: {
            needed: permissions.pAdmin,
          },
          labelId: 'field.company',
          field: 'companyId',
          initial: null,
          isUndefined: (value) => _isNil(value),
          needGetOnInit: false,
          type: SEARCHFIELD_TYPES.resource,
          filter: {
            field: 'company_id',
          },
        },
      ],
    }),

  // List features inside installations
  listFeaturesInstallation: {
    // Default list param (not used)
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [],
    },

    // Columns
    columnsQuantity: () => ({
      xs: 2,
      sm: 6,
      md: 6,
      lg: 6,
      xl: 6,
    }),
    columns: [
      {
        key: 'serial',
        labelId: 'field.serial',
        field: 'serial',
        sortable: false,
      },
      {
        key: 'name',
        labelId: 'field.name',
        field: 'name',
        sortable: false,
        className: 'hidden-xs-down',
      },
      {
        key: 'timeout',
        labelId: 'field.status',
        field: 'timeout',
        alias: 'status',
        sortable: false,
        className: 'table-content-1',
      },
      {
        key: 'syncStatus',
        labelId: 'field.syncStatus',
        field: 'syncStatus',
        sortable: false,
        className: 'table-content-1 table-content-center hidden-xs-down',
      },
      {
        key: 'network',
        labelId: 'field.network',
        field: 'network_level',
        sortable: false,
        className: 'hidden-xs-down',
      },
      {
        key: 'callNumber',
        labelId: 'field.callNumber',
        field: 'callNumber',
        sortable: false,
        className: 'hidden-xs-down',
      },
    ],

    // Search fields
    searchFields: [],
  },

  // History columns
  historyColumns: [
    {
      key: 'serial',
      labelId: 'field.serial',
      field: 'serial',
      sortable: false,
    },
    {
      key: 'begin',
      labelId: 'field.beginDate',
      field: 'begin',
      sortable: false,
    },
    {
      key: 'end',
      labelId: 'field.endDate',
      field: 'end',
      sortable: false,
    },
  ],

  // SMS Columns
  smsColumns: [
    {
      key: 'date',
      labelId: 'field.date',
      field: 'date',
      sortable: false,
    },
    {
      key: 'type',
      labelId: 'field.type',
      field: 'type',
      sortable: false,
    },
    {
      key: 'status',
      labelId: 'field.status',
      field: 'delivery_status',
      sortable: false,
      className: 'table-content-2',
    },
  ],
};

module.exports = device;



// WEBPACK FOOTER //
// ./models/device.js